
import { HimmaContext } from '../Context/HimmaContext';
import { useState, useCallback, useContext } from 'react';
import {
  CreateRestaurantCategoryRequest,
  MerchantSchema,
  CreateItemCategoryRequest,
  BlockOrUnblockRestaurantCategoryRequest,
  DeleteSubAdminsRequest,
  ToggleSubAdminStatusRequest,
  UpdateRestaurantCategoryRequest,
  CreateItemSubCategoryRequest,
} from '../client-sdk';

const userData = JSON.parse(localStorage.getItem('userData') || '{}');
const localStorageStoreId = userData?.userDetails?.storeId 

export const useApi = () => {
const api = useContext(HimmaContext)!;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add Store Catagory
  const addCategory = useCallback(
    async (categoryData: CreateRestaurantCategoryRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.createRestaurantCategory(categoryData);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while adding category';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Edit Store Category

  const editStoreCategoryId = useCallback(
    async (id: any, itemCategoryData: UpdateRestaurantCategoryRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.updateRestaurantCategory(
          id,
          itemCategoryData
        );
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while adding category';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api, setLoading, setError]
  );

  // List Store Catagory
  const getListCategories = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // Only include search parameter if it has a value
        const searchParam =
          query.search && query.search.trim() !== '' ? query.search : undefined;
        const response = await api.getRestaurantCategories(
          query.page,
          query.limit,
          // query.sortDirection,
          searchParam
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api, setLoading, setError]
  );

  // Delete Store Catagory
  const deleteStoreCategoryById = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.deleteRestaurantCategory(id);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while deleting category';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // interface BlockUnblockQuery {
  //   status: boolean;
  // }

  // Block/Unblock Store Catagory
  const blockOrUnblockStoreCategoryById = useCallback(
    async (query: { id: string[]; status: string }) => {
      setLoading(true);
      setError(null);
      try {
        const blockUnblockRestaurantCategory: BlockOrUnblockRestaurantCategoryRequest =
          {
            isActive: query.status === 'Active',
          };
        // You should be iterating or calling for each ID (assuming API supports bulk)
        const response = await Promise.all(
          query.id.map((categoryId) =>
            api.blockOrUnblockRestaurantCategory(
              categoryId,
              blockUnblockRestaurantCategory
            )
          )
        );
        return response;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const addStoreOwner = useCallback(
    async (storeOwnerData: MerchantSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.createMerchant(storeOwnerData);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while adding store owner';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const getAllStoreOwners = useCallback(
    async (params?: {
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getMerchants(
          params?.page || 0,
          params?.limit || 1000,
          undefined, // storeType
          params?.search && params.search.trim() !== ''
            ? params.search
            : undefined,
          params?.sortBy as any,
          params?.sortOrder as any
        );
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while fetching store owners';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const deleteStoreOwnerById = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.deleteMerchant(id);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while deleting store owner';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const updateStoreOwner = useCallback(
    async (id: string, storeOwnerData: MerchantSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.updateMerchant(id, storeOwnerData);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while updating store owner';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const toggleStoreOwnerStatus = useCallback(
    async (id: string, status: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.toggleMerchantActiveStatus(id, {
          isActive: status === 'active',
        });
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while toggling store owner status';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  //ItemCategory Add

  const addItemCategory = useCallback(
    async (itemCategoryData: CreateItemCategoryRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.addItemCategory(itemCategoryData);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while adding category';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  //ItemCategory List
  const getListItemCategories = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getItemCategories(
          query.page,
          query.limit,
          query.sortBy,
          query.sortOrder,
          query.statusFilter,
          query.addedByFilter,
          query.merchantId,
          query.storeId,
          // IMPORTANT: match generated client signature:
          // (page, limit, sortBy, sortOrder, statusFilter, addedByFilter, merchantId, storeId, storeType, search)
          query.storeType,
          query.search
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  //ItemCategory Edit
  const editItemCategory = useCallback(
    async (id: string, itemData: CreateItemCategoryRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.editItemCategory(id, itemData,localStorageStoreId);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  //ItemCategory Delete

  const deleteItemCategoryById = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const deleteItemCategoryByIds: DeleteSubAdminsRequest = {
          ids: query,
        };

        const response = await api.deleteCategories(deleteItemCategoryByIds,localStorageStoreId);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  //ItemCategory Block/Unblock
  const blockUnblockItemCategoryById = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const toggleSubAdminStatusRequest: ToggleSubAdminStatusRequest = {
          ids: Array.isArray(query.id) ? query.id : [query.id],
          status: query.status,
        };
        const response = await api.toggleItemCategoriesStatus(
          toggleSubAdminStatusRequest,localStorageStoreId
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  //Item Sub Category Add
  const addItemSubCategory = useCallback(
    async (itemSubCategoryData: CreateItemSubCategoryRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.addItemSubCategory(itemSubCategoryData,localStorageStoreId);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while adding category';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  //ItemCategory Edit
  const editItemSubCategory = useCallback(
    async (id: string, itemData: CreateItemSubCategoryRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.editSubItemCategory(id, itemData, localStorageStoreId);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    addCategory,
    loading,
    error,
    getListCategories,
    deleteStoreCategoryById,
    addStoreOwner,
    getAllStoreOwners,
    deleteStoreOwnerById,
    updateStoreOwner,
    toggleStoreOwnerStatus,
    addItemCategory,
    getListItemCategories,
    editItemCategory,
    blockOrUnblockStoreCategoryById,
    deleteItemCategoryById,
    blockUnblockItemCategoryById,
    editStoreCategoryId,
    addItemSubCategory,
    editItemSubCategory,
  };
};
